var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Book } from './Book.js';
import { App } from './App.js';
import * as AppService from '../service/app-service.js';
import * as BookService from '../service/book-service.js';
import * as CollectionService from '../service/collection-service.js';
import * as CollaboratorService from '../service/collaborator-service.js';
import * as UserService from '../service/user-service.js';
import * as TemplateService from '../service/template-service.js';
import { User } from './User.js';
import { Template } from './Template.js';
import { Collection } from './Collection.js';
/**
 * This is the main entry point of the [bkper-js](https://www.npmjs.com/package/bkper-js) library.
 *
 * @public
 */
export class Bkper {
    /**
     * Sets the global API configuration for all Bkper operations.
     *
     * WARNING: This configuration will be shared and should NOT be used on shared environments.
     *
     * @param config - The Config object containing API key and OAuth token providers
     */
    static setConfig(config) {
        Bkper.globalConfig = config;
    }
    /**
     * Creates a new Bkper instance with the provided configuration.
     *
     * @param config - The Config object containing API key and OAuth token providers.
     *                 If not provided, uses the global configuration set via setConfig().
     */
    constructor(config) {
        this.config = config || Bkper.globalConfig;
    }
    /**
     * Gets the current instance configuration.
     *
     * @returns The Config object for this Bkper instance
     */
    getConfig() {
        return this.config;
    }
    /**
     * Gets the [[Book]] with the specified bookId from url param.
     *
     * @param id - The universal book id - The same bookId param of URL you access at app.bkper.com
     * @param includeAccounts - Optional parameter to include accounts in the retrieved Book
     * @param includeGroups - Optional parameter to include groups in the retrieved Book
     *
     * If both includeAccounts and includeGroups are false, the Book will be returned with only the basic information.
     *
     * If includeAccounts is true, the Book will be returned with the accounts and groups.
     *
     * If includeGroups is true, the Book will be returned with the groups.
     *
     * @returns The retrieved Book
     */
    getBook(id, includeAccounts, includeGroups) {
        return __awaiter(this, void 0, void 0, function* () {
            let book = yield BookService.loadBook(id, includeAccounts, includeGroups, this.config);
            return new Book(book, this.config);
        });
    }
    /**
     * Gets all [[Books]] the user has access to.
     *
     * @param query - Optional search term to filter books
     * @returns The retrieved list of Books
     */
    getBooks(query) {
        return __awaiter(this, void 0, void 0, function* () {
            let books = yield BookService.loadBooks(query, this.config);
            return books.map(book => new Book(book, this.config));
        });
    }
    /**
     * Requests access to a Book the current user cannot access.
     *
     * @param bookId - The universal Book id
     * @param permission - The permission requested in the Book
     * @param message - An optional message to the Book owner
     */
    requestBookAccess(bookId, permission, message) {
        return __awaiter(this, void 0, void 0, function* () {
            const trimmedMessage = message === null || message === void 0 ? void 0 : message.trim();
            yield CollaboratorService.requestBookAccess(bookId, permission, trimmedMessage || undefined, this.config);
        });
    }
    /**
     * Gets all [[Collections]] the user has access to.
     *
     * @returns The retrieved list of Collections
     */
    getCollections() {
        return __awaiter(this, void 0, void 0, function* () {
            let collections = yield CollectionService.loadCollections(this.config);
            return collections.map(collection => new Collection(collection, this.config));
        });
    }
    /**
     * Gets all [[Apps]] available for the user.
     *
     * @returns The retrieved list of Apps
     */
    getApps() {
        return __awaiter(this, void 0, void 0, function* () {
            let apps = yield AppService.getApps(this.config);
            return apps.map(app => new App(app, this.config));
        });
    }
    /**
     * Gets the [[App]] with the specified id.
     *
     * @param id - The App id (agentId)
     *
     * @returns The retrieved App
     */
    getApp(id) {
        return __awaiter(this, void 0, void 0, function* () {
            let app = yield AppService.getApp(id, this.config);
            return new App(app, this.config);
        });
    }
    /**
     * Gets all [[Templates]] available for the user.
     *
     * @returns The retrieved list of Templates
     */
    getTemplates() {
        return __awaiter(this, void 0, void 0, function* () {
            let templates = yield TemplateService.getTemplates(this.config);
            return templates.map(template => new Template(template, this.config));
        });
    }
    /**
     * Gets the current logged [[User]].
     *
     * @returns The retrieved User
     */
    getUser() {
        return __awaiter(this, void 0, void 0, function* () {
            let user = yield UserService.getUser(this.config);
            return new User(user, this.config);
        });
    }
}
/** @internal */
Bkper.globalConfig = {};
//# sourceMappingURL=Bkper.js.map